/**
 * RegisterGymPage Component
 *
 * Page for users to submit a request to register a new gym.
 * Uses GymRequestForm in 'create' mode and redirects to MyGymRequestsPage on success.
 */

import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { GymRequestForm } from '@/components/gym-request/GymRequestForm';

/**
 * RegisterGymPage allows authenticated users to submit gym registration requests
 *
 * @example
 * ```tsx
 * // In router (wrapped in ProtectedRoute)
 * <Route path="/gyms/register" element={<ProtectedRoute><RegisterGymPage /></ProtectedRoute>} />
 * ```
 */
export const RegisterGymPage = () => {
  const navigate = useNavigate();

  /**
   * Handle successful form submission
   */
  const handleSuccess = () => {
    navigate('/gym-requests');
  };

  /**
   * Handle cancel action
   */
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <h1 style={titleStyle}>Register Your Gym</h1>
          <p style={descriptionStyle}>
            Submit a request to register your gym on Flash. Once submitted, an administrator will
            review your request. You will be notified when your request is approved or if any
            changes are needed.
          </p>
        </header>

        {/* Information Box */}
        <div style={infoBoxStyle}>
          <h2 style={infoTitleStyle}>What happens next?</h2>
          <ol style={infoListStyle}>
            <li style={infoItemStyle}>
              <strong>Review:</strong> Our team will review your gym registration request.
            </li>
            <li style={infoItemStyle}>
              <strong>Approval:</strong> Once approved, your gym will be created and you will become
              the gym owner.
            </li>
            <li style={infoItemStyle}>
              <strong>Setup:</strong> As an owner, you can configure your gym&apos;s wall sections,
              grade colors, and invite setters.
            </li>
          </ol>
        </div>

        {/* Registration Form */}
        <div style={formContainerStyle}>
          <h2 style={formTitleStyle}>Gym Information</h2>
          <GymRequestForm mode="create" onSuccess={handleSuccess} onCancel={handleCancel} />
        </div>
      </div>
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f9fafb',
};

const contentStyle: CSSProperties = {
  maxWidth: '700px',
  margin: '0 auto',
  padding: '20px',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1f2937',
  margin: '16px 0 8px 0',
};

const descriptionStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  lineHeight: 1.6,
  margin: 0,
};

const infoBoxStyle: CSSProperties = {
  backgroundColor: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '24px',
};

const infoTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1e40af',
  margin: '0 0 12px 0',
};

const infoListStyle: CSSProperties = {
  margin: 0,
  paddingLeft: '20px',
};

const infoItemStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#1e40af',
  lineHeight: 1.6,
  marginBottom: '8px',
};

const formContainerStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '24px',
};

const formTitleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1f2937',
  margin: '0 0 20px 0',
};

export default RegisterGymPage;
